import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { isDarkTheme, getAgentImageBaseFilter } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import useFavoriteAgent from '@/hooks/useFavoriteAgent';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import AgentOwnerMenu from '@/components/Agents/AgentOwnerMenu';
import { getBotPresentation } from '@/components/Agents/botPresentation';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

const isWeb = Platform.OS === 'web';
const AVATAR = 64;
const RAIL_WIDTH = 196;

const stop = (event) => {
  event?.stopPropagation?.();
  event?.preventDefault?.();
};

function FavoriteStar({ agentId, colors }) {
  const { isSaved, toggleFavorite } = useFavoriteAgent(agentId);
  const { t } = useTranslation();
  return (
    <Tooltip label={t(isSaved ? 'pages.agents.removeFavoriteTooltip' : 'pages.agents.addFavoriteTooltip')}>
      <Pressable
        onPress={(event) => {
          stop(event);
          toggleFavorite();
        }}
        onPressIn={stop}
        accessibilityRole="button"
        accessibilityState={{ selected: isSaved }}
        accessibilityLabel={isSaved ? t('nav.routes.agentChat.unsave') : t('pages.agents.colFavorite')}
        style={({ hovered, pressed }) => [
          {
            width: 40,
            height: 40,
            borderRadius: 9,
            alignItems: 'center',
            justifyContent: 'center',
          },
          (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
        ]}
      >
        <Icon
          name="star"
          solid={isSaved}
          size={15}
          color={isSaved ? colors.text_color_primary : colors.text_color_tertiary}
        />
      </Pressable>
    </Tooltip>
  );
}

function AgentCard({
  agent,
  onPress,
  onUseAgent,
  isLoading,
  width: explicitWidth,
  fillHeight = true,
  compact = false,
  variant = 'default',
}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const startAgentWorkChat = useStartAgentWorkChat();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isMenuOpenRef = useRef(false);
  const menuClosedAtRef = useRef(0);
  const isRail = variant === 'rail';
  const styles = useMemo(
    () => createStyles(colors, getCatalogPalette(theme), explicitWidth, fillHeight, isRail, compact),
    [colors, theme, explicitWidth, fillHeight, isRail, compact],
  );
  const presentation = getBotPresentation(agent, isDarkTheme(theme));
  const imageFilter = getAgentImageBaseFilter(theme, !!agent?.flags?.isImageInvertable);
  const filteredStyle = agent?.flags?.isImageInvertable && imageFilter ? { filter: imageFilter } : null;

  const handleMenuStateChange = useCallback((open) => {
    isMenuOpenRef.current = open;
    if (!open) menuClosedAtRef.current = Date.now();
  }, []);

  const handleOpenChat = useCallback(() => {
    if (isMenuOpenRef.current || Date.now() - menuClosedAtRef.current < 350) return;
    if (onUseAgent) onUseAgent(agent);
    else if (onPress) onPress(agent);
    else startAgentWorkChat(agent, { source: 'agent_card_use_agent', chooseMode: true });
  }, [onUseAgent, onPress, agent, startAgentWorkChat]);

  if (isLoading) {
    return (
      <View style={styles.card} aria-busy={true}>
        <View style={[styles.skeleton, styles.avatar]} />
        <View style={styles.identity}>
          <View style={[styles.skeleton, { height: 22, width: '75%' }]} />
          <View style={[styles.skeleton, { height: 18, width: '90%' }]} />
        </View>
      </View>
    );
  }
  if (!agent) return null;

  const useBotLabel = t('pages.agents.openBot');

  return (
    <View style={[styles.card, hovered && styles.cardHovered, focused && styles.cardFocused]}>
      <Pressable
        onPress={handleOpenChat}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        accessibilityRole="button"
        accessibilityLabel={presentation.name}
        accessibilityHint={useBotLabel}
        style={[StyleSheet.absoluteFillObject, { borderRadius: 20 }]}
      />
      <View style={styles.topRow} pointerEvents="box-none">
        <View style={[styles.avatar, { backgroundColor: presentation.useAvatar ? 'transparent' : presentation.background }, presentation.useAvatar && isWeb && filteredStyle]} pointerEvents="none">
          {presentation.useAvatar ? (
            <Image source={{ uri: agent.avatarPath }} style={[styles.avatarImage, !isWeb && filteredStyle]} contentFit="cover" />
          ) : (
            <Icon name={presentation.icon} size={25} color={presentation.foreground} />
          )}
        </View>
      </View>
      <View style={styles.identity} pointerEvents="none">
        <Text weight="bold" color="primary" numberOfLines={2} style={styles.title}>
          {presentation.name}
        </Text>
        {!!agent.flags?.isPlusOnly && <View style={styles.plusPill}><Text size="xxs" weight="bold" color="secondary">PLUS</Text></View>}
        <View style={styles.summaryRow}>
          {!!presentation.summary && <Text size="s" color="secondary" numberOfLines={2} style={styles.description}>{presentation.summary}</Text>}
          <View style={[styles.openIndicator, (hovered || focused) && styles.openIndicatorActive]}>
            <Icon name="arrow-right" size={13} color={colors.text_color_secondary} />
          </View>
        </View>
      </View>
      <View style={styles.actions}>
        {!agent.isOwner && <FavoriteStar agentId={agent.id} colors={colors} />}
        <AgentOwnerMenu agent={agent} size={40} iconSize={15} onMenuStateChange={handleMenuStateChange} />
      </View>
    </View>
  );
}

const createStyles = (colors, palette, explicitWidth, fillHeight, isRail, compact) => StyleSheet.create({
  card: {
    width: explicitWidth != null ? explicitWidth : (isRail ? RAIL_WIDTH : '100%'),
    // Percentage heights expand against the native ScrollView's content,
    // pushing the action far below the card. Web grids have bounded rows.
    ...(isWeb && fillHeight && !isRail ? { alignSelf: 'stretch', height: '100%' } : {}),
    minWidth: 0,
    minHeight: compact ? 112 : isRail ? 196 : 184,
    flexDirection: compact ? 'row' : 'column',
    alignItems: compact ? 'center' : 'stretch',
    padding: compact ? 16 : 22,
    gap: compact ? 14 : 22,
    borderWidth: 1,
    borderRadius: 20,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    ...(isWeb ? { transition: 'border-color 150ms ease, background-color 150ms ease' } : {}),
  },
  cardHovered: { borderColor: palette.activeBorder },
  cardFocused: { borderColor: colors.text_color_link },
  topRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 6,
  },
  avatar: {
    width: compact ? 48 : AVATAR,
    height: compact ? 48 : AVATAR,
    borderRadius: 15,
    backgroundColor: palette.preview,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImage: { width: compact ? 48 : AVATAR, height: compact ? 48 : AVATAR },
  actions: { position: 'absolute', top: compact ? 8 : 16, right: compact ? 8 : 16, flexDirection: 'row', alignItems: 'center', gap: 2 },
  identity: { flex: 1, minWidth: 0, justifyContent: 'flex-end', gap: 5, paddingRight: compact ? 20 : 0 },
  title: { fontSize: compact ? 16 : 18, lineHeight: compact ? 22 : 24, letterSpacing: -0.4 },
  plusPill: {
    alignSelf: 'flex-start',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: palette.border,
  },
  summaryRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  description: { flex: 1, lineHeight: 19 },
  openIndicator: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', marginRight: -4 },
  openIndicatorActive: { backgroundColor: palette.hover },
  skeleton: { backgroundColor: palette.preview, borderRadius: 8 },
});

AgentCard.propTypes = {
  agent: PropTypes.object,
  onPress: PropTypes.func,
  onUseAgent: PropTypes.func,
  isLoading: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fillHeight: PropTypes.bool,
  compact: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'rail']),
};

export default React.memo(AgentCard);
